import { useQueryClient } from '@tanstack/react-query';
import type { AuthMeResponse } from '@zrsystem/contracts';
import type { ContextSwitcherSection } from '@zrsystem/ui';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  buildAccessesFromAuthMe,
  resolveActiveContextFromAuthMe,
} from '@/app/navigation-context/build-navigation-access.js';
import {
  activeEntityIdFromAccess,
  buildContextLabel,
  buildNetworkSummaries,
  buildPlaceSummaries,
  buildSwitchContextBody,
  buildTenantSummaries,
  contextSwitchSuccessMessage,
  hasPlatformAccess,
  homePathForLevel,
  isStaticSwitcher,
  navigationLevelFromAccess,
  shouldRemintPlaceAccessToken,
} from '@/app/navigation-context/navigation-context.utils.js';
import {
  clearPersistedNavigationContext,
  readPersistedNavigationContext,
  writePersistedNavigationContext,
} from '@/app/navigation-context/persist-navigation-context.js';
import type { NavigationContextValue, NavigationLevel } from '@/app/navigation-context/types.js';
import { NavigationContext } from '@/app/navigation-context/use-navigation-context.js';
import { useAuth } from '@/app/providers/auth-provider.js';
import { useSurfaceNavigation } from '@/app/surfaces/use-surface-navigation.js';
import { switchContextRequest } from '@/shared/api/auth-switch.api.js';
import { setAccessToken } from '@/shared/auth/auth-storage.js';
import { notifyCreated } from '@/shared/feedback/notify.js';
import { setActivePlaceId } from '@/shared/workspace/active-place-bridge.js';

interface NavigationContextProviderProps {
  children: ReactNode;
}

function resolveBootState(user: AuthMeResponse): {
  level: NavigationLevel;
  activeEntityId: string | null;
} {
  const persisted = readPersistedNavigationContext(user.id);
  const accesses = buildAccessesFromAuthMe(user);

  if (persisted) {
    const allowed =
      (persisted.level === 'place' &&
        accesses.some((a) => a.scopeType === 'PLACE' && a.placeId === persisted.placeId)) ||
      (persisted.level === 'tenant' &&
        accesses.some((a) => a.scopeType === 'TENANT' && a.tenantId === persisted.tenantId)) ||
      (persisted.level === 'network' &&
        accesses.some(
          (a) =>
            a.scopeType === 'ORGANIZATION_GROUP' && a.organizationGroupId === persisted.networkId,
        )) ||
      (persisted.level === 'platform' && accesses.some((a) => a.scopeType === 'SYSTEM'));

    if (allowed) {
      return {
        level: persisted.level,
        activeEntityId: persisted.placeId ?? persisted.tenantId ?? persisted.networkId ?? null,
      };
    }
  }

  const active = resolveActiveContextFromAuthMe(user);
  if (!active) {
    return { level: 'place', activeEntityId: null };
  }

  return {
    level: navigationLevelFromAccess(active),
    activeEntityId: activeEntityIdFromAccess(active),
  };
}

export function NavigationContextProvider({ children }: NavigationContextProviderProps) {
  const { status, user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { closeAllQuickPanels } = useSurfaceNavigation();

  const accesses = useMemo(() => (user ? buildAccessesFromAuthMe(user) : []), [user]);

  const allowedPlaces = useMemo(() => buildPlaceSummaries(accesses), [accesses]);
  const allowedTenants = useMemo(() => buildTenantSummaries(accesses), [accesses]);
  const allowedNetworks = useMemo(() => buildNetworkSummaries(accesses), [accesses]);
  const platformAccess = useMemo(() => hasPlatformAccess(accesses), [accesses]);

  const [level, setLevelState] = useState<NavigationLevel>('place');
  const [activeEntityId, setActiveEntityId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (status !== 'authenticated' || !user) {
      setIsReady(false);
      setLevelState('place');
      setActiveEntityId(null);
      return;
    }

    const boot = resolveBootState(user);
    const applyBoot = (placeId: string | null): void => {
      setLevelState(boot.level);
      setActiveEntityId(placeId ?? boot.activeEntityId);
      if (boot.level === 'place' && (placeId ?? boot.activeEntityId)) {
        setActivePlaceId(placeId ?? boot.activeEntityId);
      }
      setIsReady(true);
    };

    if (boot.level === 'place' && shouldRemintPlaceAccessToken(user.placeId, boot.activeEntityId)) {
      const sync = { cancelled: false };
      void (async () => {
        try {
          const response = await switchContextRequest(
            buildSwitchContextBody('place', boot.activeEntityId ?? undefined),
          );
          if (sync.cancelled) return;
          setAccessToken(response.accessToken);
          await refreshUser();
        } catch {
          if (sync.cancelled) return;
          applyBoot(user.placeId ?? null);
        }
      })();
      return () => {
        sync.cancelled = true;
      };
    }

    applyBoot(boot.activeEntityId);
    return undefined;
  }, [refreshUser, status, user]);

  const setLevel = useCallback(
    async (nextLevel: NavigationLevel, entityId?: string) => {
      if (!user) {
        return;
      }

      const resolvedEntityId =
        entityId ??
        (nextLevel === 'place'
          ? allowedPlaces[0]?.id
          : nextLevel === 'tenant'
            ? allowedTenants[0]?.id
            : nextLevel === 'network'
              ? allowedNetworks[0]?.id
              : null);

      writePersistedNavigationContext(user.id, {
        level: nextLevel,
        placeId: nextLevel === 'place' ? (resolvedEntityId ?? null) : null,
        tenantId: nextLevel === 'tenant' ? (resolvedEntityId ?? null) : null,
        networkId: nextLevel === 'network' ? (resolvedEntityId ?? null) : null,
      });

      const destinationName = buildContextLabel(
        nextLevel,
        resolvedEntityId ?? null,
        allowedPlaces,
        allowedTenants,
        allowedNetworks,
      );

      try {
        const response = await switchContextRequest(
          buildSwitchContextBody(nextLevel, resolvedEntityId ?? undefined),
        );
        setAccessToken(response.accessToken);
        await refreshUser();
      } catch {
        return;
      }

      notifyCreated(contextSwitchSuccessMessage(destinationName));

      setLevelState(nextLevel);
      setActiveEntityId(resolvedEntityId ?? null);

      if (nextLevel === 'place' && resolvedEntityId) {
        setActivePlaceId(resolvedEntityId);
      } else {
        setActivePlaceId(null);
      }

      closeAllQuickPanels();
      await queryClient.cancelQueries();
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return (
            key === 'place' ||
            key === 'tenant' ||
            key === 'network' ||
            key === 'platform' ||
            key === 'conversation'
          );
        },
      });

      void navigate(homePathForLevel(nextLevel, resolvedEntityId ?? null));
    },
    [
      allowedNetworks,
      allowedPlaces,
      allowedTenants,
      closeAllQuickPanels,
      navigate,
      queryClient,
      refreshUser,
      user,
    ],
  );

  useEffect(() => {
    if (status === 'unauthenticated' && user?.id) {
      clearPersistedNavigationContext(user.id);
    }
  }, [status, user?.id]);

  const contextLabel = useMemo(
    () => buildContextLabel(level, activeEntityId, allowedPlaces, allowedTenants, allowedNetworks),
    [activeEntityId, allowedNetworks, allowedPlaces, allowedTenants, level],
  );

  const isStaticSwitcherValue = useMemo(
    () => isStaticSwitcher(allowedPlaces, allowedTenants, allowedNetworks, platformAccess),
    [allowedNetworks, allowedPlaces, allowedTenants, platformAccess],
  );

  const switcherSections = useMemo((): ContextSwitcherSection[] => {
    const sections: ContextSwitcherSection[] = [];

    if (allowedPlaces.length > 0) {
      sections.push({
        level: 'place',
        label: 'Oficinas',
        activeId: level === 'place' ? activeEntityId : null,
        items: allowedPlaces.map((place) => ({
          id: place.id,
          label: place.name,
          ...(place.subtitle !== undefined ? { subtitle: place.subtitle } : {}),
        })),
      });
    }

    if (allowedTenants.length > 0) {
      sections.push({
        level: 'tenant',
        label: 'Organização',
        activeId: level === 'tenant' ? activeEntityId : null,
        items: allowedTenants.map((tenant) => ({
          id: tenant.id,
          label: tenant.name,
          ...(tenant.subtitle !== undefined ? { subtitle: tenant.subtitle } : {}),
        })),
      });
    }

    if (allowedNetworks.length > 0) {
      sections.push({
        level: 'network',
        label: 'Rede',
        activeId: level === 'network' ? activeEntityId : null,
        items: allowedNetworks.map((network) => ({
          id: network.id,
          label: network.name,
          ...(network.subtitle !== undefined ? { subtitle: network.subtitle } : {}),
        })),
      });
    }

    if (platformAccess) {
      sections.push({
        level: 'platform',
        label: 'Plataforma',
        activeId: level === 'platform' ? 'system' : null,
        items: [
          {
            id: 'system',
            label: 'Administração',
            subtitle: 'Entidades e acessos',
          },
        ],
      });
    }

    return sections;
  }, [activeEntityId, allowedNetworks, allowedPlaces, allowedTenants, level, platformAccess]);

  const value = useMemo<NavigationContextValue>(
    () => ({
      level,
      activeEntityId,
      allowedPlaces,
      allowedTenants,
      allowedNetworks,
      hasPlatformAccess: platformAccess,
      contextLabel,
      isStaticSwitcher: isStaticSwitcherValue,
      isReady,
      switcherSections,
      setLevel,
    }),
    [
      activeEntityId,
      allowedNetworks,
      allowedPlaces,
      allowedTenants,
      contextLabel,
      isReady,
      isStaticSwitcherValue,
      level,
      platformAccess,
      setLevel,
      switcherSections,
    ],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
